'use client';

import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  tablePlugin,
  codeBlockPlugin,
  linkPlugin,
  linkDialogPlugin,
  diffSourcePlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  CodeToggle,
  ListsToggle,
  BlockTypeSelect,
  CreateLink,
  InsertTable,
  InsertThematicBreak,
  DiffSourceToggleWrapper,
  type MDXEditorMethods,
  useCellValues,
  currentSelection$,
  activeEditor$,
} from '@mdxeditor/editor';
import { LexicalNode, ElementNode, ParagraphNode } from 'lexical';
import '@mdxeditor/editor/style.css';
import { forwardRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Save, Loader2, Copy, Check } from 'lucide-react';

export interface MarkdownEditorProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  onSelectionChange?: (selectedText: string, paragraphText: string, paragraphIndex: number) => void;
  onMouseUp?: (selectedText: string, paragraphText: string, paragraphIndex: number) => void;
  diffMarkdown?: string; // Original markdown for comparison in diff mode
  viewMode?: 'rich-text' | 'diff' | 'source'; // Initial editor view mode
  readOnlyDiff?: boolean; // Make diff mode read-only (default: true)
  onSave?: () => void | Promise<void>; // Save handler
  hasChanges?: boolean; // Whether there are unsaved changes
  isSaving?: boolean; // Whether save is in progress
  onCopy?: () => void | Promise<void>; // Copy handler
}

// Mouse event handler component to trigger on mouse up after text selection
const MouseEventHandler = ({
  onMouseUp,
}: {
  onMouseUp?: (selectedText: string, paragraphText: string, paragraphIndex: number) => void;
}) => {
  const [currentSelection, activeEditor] = useCellValues(currentSelection$, activeEditor$);

  useEffect(() => {
    const handleMouseUp = () => {
      if (!onMouseUp || !activeEditor || !currentSelection) return;

      try {
        activeEditor.getEditorState().read(() => {
          const selectedText = currentSelection.getTextContent();
          let paragraphText = '';
          let paragraphIndex = -1;

          const anchorNode = currentSelection.anchor.getNode();
          const focusNode = currentSelection.focus.getNode();

          // Find the common ancestor block node (e.g., paragraph, heading)
          let commonAncestor: LexicalNode | null = anchorNode.getCommonAncestor(focusNode);

          // Traverse up to find a block parent if the common ancestor is not a block or is a text node
          while (
            commonAncestor &&
            !(commonAncestor instanceof ParagraphNode) &&
            !(commonAncestor instanceof ElementNode)
          ) {
            commonAncestor = commonAncestor.getParent();
          }

          if (commonAncestor) {
            paragraphText = commonAncestor.getTextContent();

            // Get paragraph index within parent (0-based)
            const parent = commonAncestor.getParent();
            if (parent) {
              const siblings = parent.getChildren();
              paragraphIndex = siblings.findIndex(
                (node) => node.getKey() === commonAncestor!.getKey()
              );
            }
          }

          if (selectedText.trim()) {
            onMouseUp(selectedText, paragraphText, paragraphIndex);
          }
        });
      } catch (error) {
        console.error('[MouseEventHandler] Error extracting text:', error);
      }
    };

    const editorRootElement = activeEditor?.getRootElement();
    if (editorRootElement) {
      editorRootElement.addEventListener('mouseup', handleMouseUp);
      return () => {
        editorRootElement.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [onMouseUp, activeEditor, currentSelection]);

  return null;
};

// Save button component for toolbar
const SaveButton = ({
  onSave,
  hasChanges,
  isSaving,
}: {
  onSave?: () => void | Promise<void>;
  hasChanges?: boolean;
  isSaving?: boolean;
}) => {
  if (!onSave) return null;

  const handleClick = async () => {
    if (isSaving || !hasChanges) return;
    await onSave();
  };

  return (
    <button
      onClick={handleClick}
      disabled={!hasChanges || isSaving}
      className="flex items-center justify-center p-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:bg-gray-100 enabled:text-gray-900 disabled:text-gray-400"
      title={isSaving ? 'Saving...' : hasChanges ? 'Save changes' : 'No changes to save'}
    >
      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
    </button>
  );
};

// Copy button component for toolbar
const CopyButton = ({ onCopy }: { onCopy?: () => void | Promise<void> }) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'copied'>('idle');

  if (!onCopy) return null;

  const handleClick = async () => {
    if (copyStatus === 'copying') return;

    setCopyStatus('copying');
    try {
      await onCopy();
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      setCopyStatus('idle');
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={copyStatus === 'copying'}
      className="flex items-center justify-center p-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:bg-gray-100 enabled:text-gray-900"
      title={
        copyStatus === 'copying'
          ? 'Copying...'
          : copyStatus === 'copied'
            ? 'Copied to clipboard!'
            : 'Copy markdown to clipboard'
      }
    >
      {copyStatus === 'copied' ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : copyStatus === 'copying' ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
};

export const MarkdownEditor = forwardRef<MDXEditorMethods, MarkdownEditorProps>(
  (
    {
      value,
      onChange,
      placeholder,
      readOnly = false,
      className,
      onSelectionChange,
      onMouseUp,
      diffMarkdown,
      viewMode,
      readOnlyDiff,
      onSave,
      hasChanges,
      isSaving,
      onCopy,
    },
    ref
  ) => {
    return (
      <div className={cn('border rounded-lg overflow-auto max-h-[80vh]', className)}>
        <MDXEditor
          ref={ref}
          markdown={value}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={placeholder}
          contentEditableClassName="prose prose-sm max-w-none min-h-[400px] p-4"
          plugins={[
            diffSourcePlugin({
              diffMarkdown: diffMarkdown || '',
              viewMode: viewMode || 'rich-text',
              readOnlyDiff: readOnlyDiff ?? true,
            }),
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            markdownShortcutPlugin(),
            tablePlugin(),
            codeBlockPlugin({ defaultCodeBlockLanguage: 'txt' }),
            linkPlugin(),
            linkDialogPlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <DiffSourceToggleWrapper>
                    <UndoRedo />
                  </DiffSourceToggleWrapper>
                  <BoldItalicUnderlineToggles />
                  <CodeToggle />
                  <BlockTypeSelect />
                  <ListsToggle />
                  <CreateLink />
                  <InsertTable />
                  <InsertThematicBreak />
                  <SaveButton onSave={onSave} hasChanges={hasChanges} isSaving={isSaving} />
                  <CopyButton onCopy={onCopy} />
                  {(onSelectionChange || onMouseUp) && (
                    <MouseEventHandler onMouseUp={onMouseUp || onSelectionChange} />
                  )}
                </>
              ),
            }),
          ]}
        />
      </div>
    );
  }
);

MarkdownEditor.displayName = 'MarkdownEditor';
